/**
 * PHASE 8A — A8-P1-1
 *
 * Centralised, deduplicated access-token refresh for the admin client.
 *
 * Contract:
 *   - On a 401 response from any authenticated request, the admin client
 *     performs exactly ONE refresh attempt using the refresh token
 *     currently in `tokenStorage`.
 *   - If multiple in-flight requests receive a 401 concurrently, only one
 *     POST /admin/auth/refresh is dispatched; the others wait on the same
 *     promise and share its result.
 *   - Refresh-mutex prevents refresh storms.
 *   - On refresh success: the new access token (and the rotated refresh
 *     token, if any) are written to `tokenStorage` and every queued
 *     request is retried exactly once.
 *   - On refresh failure: `tokenStorage.clear()` is called via the
 *     supplied `onSessionInvalid` hook (so the AuthContext can transition
 *     to `unauthenticated`); every queued request rejects with the
 *     original 401 error; no retry storm ever happens.
 *   - Calling `refreshAccessToken()` directly is also supported so the
 *     AuthContext can prepare a session before its first request.
 *
 * Phase 8A scope: this file is dependency-free; no React, no router,
 * no test mocks. It uses `fetch` directly so it can be unit-tested.
 */

import { config } from '@/config/appConfig';

const REFRESH_URL = `${config.apiBase}/admin/auth/refresh`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RefreshSuccess {
  ok: true;
  accessToken: string;
  expiresAt: string;
}

export interface RefreshFailure {
  ok: false;
  /** HTTP status, or 0 for network errors. */
  status: number;
  message: string;
}

export type RefreshResult = RefreshSuccess | RefreshFailure;

export interface RefreshDeps {
  /** Read the current refresh token (or null when unauthenticated). */
  getRefreshToken: () => string | null;
  /** Persist the newly-issued access token. Return value ignored; this
   *  hook MUST NOT throw, otherwise the refresh promise will reject. */
  setSession: (s: { accessToken: string; expiresAt: string }) => void;
  /** Clear local session and notify the AuthContext. MUST NOT throw. */
  onSessionInvalid: () => void;
  /** Injectable fetch (for tests). Defaults to globalThis.fetch. */
  fetch?: typeof fetch;
}

// ---------------------------------------------------------------------------
// Module-level refresh mutex: shared across every instance.
// ---------------------------------------------------------------------------

type RefreshListener = (result: RefreshResult) => void;

let inflightRefresh: Promise<RefreshResult> | null = null;
const waiters: RefreshListener[] = [];

/**
 * Resolve every queued waiter with the given refresh result and clear the
 * in-flight state. Safe to call with an already-cleared state (no-op).
 */
function settleWaiters(result: RefreshResult): void {
  while (waiters.length > 0) {
    const next = waiters.shift()!;
    try {
      next(result);
    } catch {
      // A listener throw must never block the next one; test-only safety.
    }
  }
  inflightRefresh = null;
}

/**
 * Returns a single shared promise for one refresh attempt. All concurrent
 * callers receive the same promise and therefore the same result.
 */
async function performRefresh(deps: RefreshDeps): Promise<RefreshResult> {
  const refreshToken = deps.getRefreshToken();
  if (!refreshToken) {
    return { ok: false, status: 0, message: 'No refresh token available.' };
  }
  const f = deps.fetch ?? globalThis.fetch;
  let response: Response;
  try {
    response = await f(REFRESH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (err) {
    return {
      ok: false,
      status: 0,
      message: err instanceof Error ? err.message : 'Network error during refresh.',
    };
  }

  if (!response.ok) {
    let payload: { error?: { code?: string; message?: string } } | null = null;
    try {
      payload = (await response.json()) as { error?: { code?: string; message?: string } };
    } catch {
      // Server may not return JSON; leave payload null.
    }
    return {
      ok: false,
      status: response.status,
      message: payload?.error?.message ?? `Refresh failed with status ${response.status}.`,
    };
  }

  let body: { accessToken?: string; expiresAt?: string } | null = null;
  try {
    body = (await response.json()) as { accessToken?: string; expiresAt?: string };
  } catch {
    return { ok: false, status: response.status, message: 'Refresh response was not JSON.' };
  }

  // Server envelope unwrap: either { data: { accessToken, expiresAt } } or
  // a direct payload.
  const inner =
    body && typeof body === 'object' && 'data' in body
      ? ((body as unknown as { data: { accessToken?: string; expiresAt?: string } }).data ?? null)
      : body;

  const accessToken = (inner && typeof inner.accessToken === 'string' && inner.accessToken) || '';
  if (!accessToken) {
    return { ok: false, status: response.status, message: 'Refresh response missing accessToken.' };
  }
  const expiresAt =
    (inner && typeof inner.expiresAt === 'string' && inner.expiresAt) ||
    new Date(Date.now() + 15 * 60 * 1000).toISOString();

  deps.setSession({ accessToken, expiresAt });
  return { ok: true, accessToken, expiresAt };
}

/**
 * Public entry-point used by the HTTP wrapper. Deduplicates concurrent
 * refreshes: subsequent callers wait on the same promise.
 */
export function refreshAccessToken(deps: RefreshDeps): Promise<RefreshResult> {
  if (inflightRefresh) {
    return new Promise<RefreshResult>((resolve) => {
      waiters.push(resolve);
    });
  }
  inflightRefresh = (async () => {
    let result: RefreshResult;
    try {
      result = await performRefresh(deps);
    } catch (err) {
      result = {
        ok: false,
        status: 0,
        message: err instanceof Error ? err.message : 'Unknown refresh error.',
      };
    }
    if (!result.ok) {
      // Best-effort cleanup. The hook must never throw; but if it does,
      // we log and move on so we never block the caller.
      try {
        deps.onSessionInvalid();
      } catch {
        // ignored
      }
    }
    settleWaiters(result);
    return result;
  })();
  return inflightRefresh;
}

/**
 * Test helper: clears module-level state between unit tests so a previous
 * failed refresh doesn't poison the next one.
 */
export function __resetRefreshStateForTests(): void {
  inflightRefresh = null;
  while (waiters.length > 0) waiters.pop();
}
